import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Person,
  ExpandMore,
  ExpandLess,
  Phone,
  Email,
  CheckCircle,
  Cancel,
  Close,
  Add,
  PersonAdd,
  OpenInNew,
  Visibility,
} from '@mui/icons-material';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  FormControl,
  FormControlLabel,
  InputLabel,
  TextField,
  Menu,
  CircularProgress,
  Divider,
  Switch,
  Tooltip,
} from '@mui/material';
import { ROLES, getRoleDisplayName, getRoleBadgeColor } from '@utils/roleUtils';
import type {
  // AdminOrganizationDetailResponse,
  AdminProviderResponse,
} from '@store/generated/organizations';

import { AppHeader } from '../components/app-header';
import { CardTitle } from '../components/card-title';
import { ExpandableDataTable } from '../components/expandable-data-table';
import { LoadingState } from '../components/loading-state';
import { formatCents, formatDateTime, formatRelativeTime } from '../utils/formatting';
import { OrganizationTemplatesSection } from '../components/organization-templates-section';
import { OrganizationEncounterTypesSection } from '../components/organization-encounter-types-section';
import { OrganizationPoliciesSection } from '../components/organization-policies-section';
import { OrganizationBundleAssignmentsSection } from '../components/organization-bundle-assignments-section';
import { OrganizationRolePermissionsSection } from '../components/organization-role-permissions-section';
import {
  OrganizationSubscriptionCard,
  toSubscriptionState,
} from '../components/organization-subscription-card';
import { OrgPassport } from '../components/org-page-redesign/OrgPassport';
import { WorkspaceSidebar } from '../components/org-page-redesign/WorkspaceSidebar';
import { PrescribingProxyToggle } from '../components/prescribing-proxy-toggle';
import { OrganizationBillingHistoryCard } from '../components/organization-billing-history-card';
import { RecentActivityCard } from '../components/recent-activity-card';
import { OrganizationUpcomingInvoiceCard } from '../components/organization-upcoming-invoice-card';
import { OrganizationBillingSection } from '../components/organization-billing-section';
import { OrganizationBillingTasksSection } from '../components/organization-billing-tasks-section';
import {
  ApplyCreditDialog,
  ApplyDiscountDialog,
  CreditBalanceCard,
  DiscountCard,
  EditDiscountLabelsDialog,
  InvoiceRefundAction,
  RefundDialog,
} from '../components/org-page-redesign/BillingAdjustments';
import type {
  CreditTransactionRow,
  DiscountRow,
  InvoiceRow,
  ItemDiscountRow,
  SubscriptionLineItem,
} from '../components/org-page-redesign/types';
import { OrganizationMetricsSection } from '../components/organization-metrics-section';
import { OrganizationInvitationsSection } from '../components/organization-invitations-section';
import { OrganizationMembersList } from '../components/organization-members-list';
import { OrganizationPayerDocsSection } from '../components/organization-payer-docs-section';
import { OrganizationDosespotPatientsSection } from '../components/organization-dosespot-patients-section';
import { OrganizationStatsigGatesSection } from '../components/organization-statsig-gates-section';
import { DataExportSection } from '../components/data-export-section';
import { DataImportSection } from '../components/data-import-section';
import {
  ORG_NAV_GROUPS,
  ORG_NAV_LINK_KEYS,
  ORG_SECTION_KEYS,
  orgRailDestination,
} from '../components/org-nav';
import { AdminClinicDocumentsEmbedded } from '@features/clinic-documents';
import { AdminIntakeHubEmbedded } from '@features/intake-hub';
import { AdminJotAuditDashboardEmbedded } from '@features/jotaudit';
// TODO: Re-enable forms functionality when ready to support forms in addition to templates
// import { OrganizationFormsSection } from '../components/organization-forms-section';
import {
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  useGetV2AdminOrganizationsByOrganizationIdProvidersQuery,
  useDeleteV2AdminOrganizationsByOrganizationIdMembersAndUserIdMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdReactivateMutation,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdSyncSubscriptionMutation,
  useGetV2AdminOrganizationsByOrganizationIdSubscriptionDashboardUrlQuery,
  useGetV2AdminOrganizationsByOrganizationIdInvoicesQuery,
  usePostV2AdminOrganizationsByOrganizationIdExtendTrialMutation,
  usePostV2AdminOrganizationsByOrganizationIdCreateSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdCancelSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdReactivateSubscriptionMutation,
  useGetV2AdminOrganizationsByOrganizationIdCreditsQuery,
  usePostV2AdminOrganizationsByOrganizationIdCreditsMutation,
  useGetV2AdminOrganizationsByOrganizationIdDiscountsQuery,
  usePostV2AdminOrganizationsByOrganizationIdDiscountsMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdDiscountsMutation,
  usePostV2AdminOrganizationsByOrganizationIdRefundsMutation,
  usePatchV2AdminOrganizationsByOrganizationIdDiscountsMutation,
  usePostV2AdminOrganizationsByOrganizationIdMergeMutation,
  usePatchV2AdminOrganizationsByOrganizationIdMutation,
  useSearchProvidersQuery,
  useGetV2AdminOrganizationsByOrganizationIdPatientVisibilityQuery,
  usePatchV2AdminOrganizationsByOrganizationIdPatientVisibilityMutation,
  useGetAdminMembershipInfoQuery,
  useUpdateAdminMembershipInfoMutation,
} from '../store/hooks';
import { useSnackbar } from 'notistack';

const NAV_GROUPS = ORG_NAV_GROUPS;
const NAV_LINK_KEYS = ORG_NAV_LINK_KEYS;
const SECTION_KEYS = ORG_SECTION_KEYS;

// A malformed timestamp must degrade to "no date", not throw: these feed
// useMemo adapters, and Date.toISOString on an out-of-range value would take
// down the whole page via the route error boundary.
function epochToIso(seconds: number | null | undefined): string | null {
  if (!seconds) return null;
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/**
 * Inline co-sign toggle for admin expanded row content.
 * Uses hooks to fetch/update membership info for the given member.
 */
function AdminCosignToggle({ organizationId, userId }: { organizationId: string; userId: string }) {
  const { data, isLoading } = useGetAdminMembershipInfoQuery(
    { organizationId, userId },
    { skip: !organizationId || !userId },
  );
  const [updateMembershipInfo, { isLoading: isUpdating }] = useUpdateAdminMembershipInfoMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleToggle = async (checked: boolean) => {
    try {
      await updateMembershipInfo({
        organizationId,
        userId,
        updateMembershipInfoRequest: { needs_cosign: checked },
      }).unwrap();
      enqueueSnackbar(checked ? 'Co-sign requirement enabled' : 'Co-sign requirement removed', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('Failed to update co-sign setting', { variant: 'error' });
    }
  };

  if (isLoading) return <CircularProgress size={16} />;

  return (
    <FormControlLabel
      control={
        <Switch
          checked={data?.needs_cosign ?? false}
          onChange={e => void handleToggle(e.target.checked)}
          disabled={isUpdating}
          size="small"
        />
      }
      label={<span className="text-sm text-gray-700">Require co-signature</span>}
    />
  );
}

export default function OrganizationDetailPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  // The page (and Resources sub-tabs) is addressable via ?tab= / ?sub= so
  // deep-links land on the right tab and back-navigation from the Resource
  // Explorer's list/detail routes returns here instead of dead-ending.
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    userId?: string;
    role?: string;
    action?: 'remove' | 'add';
  }>({ open: false });
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>(ROLES.CLINICIAN);
  const [hasSearched, setHasSearched] = useState(false);
  const [roleMenuAnchor, setRoleMenuAnchor] = useState<HTMLElement | null>(null);
  const [roleMenuProviderId, setRoleMenuProviderId] = useState<string | null>(null);
  const [extendTrialDialog, setExtendTrialDialog] = useState(false);
  const [trialDaysToAdd, setTrialDaysToAdd] = useState<number>(7);
  const [createSubscriptionDialog, setCreateSubscriptionDialog] = useState(false);
  const [subscriptionTrialDays, setSubscriptionTrialDays] = useState<number | null>(null);
  const [cancelSubscriptionDialog, setCancelSubscriptionDialog] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelConfirmText, setCancelConfirmText] = useState('');
  const [applyCreditDialog, setApplyCreditDialog] = useState(false);
  const [applyDiscountDialog, setApplyDiscountDialog] = useState(false);
  const [refundInvoice, setRefundInvoice] = useState<InvoiceRow | null>(null);
  const [editingDiscount, setEditingDiscount] = useState<DiscountRow | null>(null);
  const [inactiveUsersExpanded, setInactiveUsersExpanded] = useState(false);
  const [loadingOperations, setLoadingOperations] = useState<{ [key: string]: boolean }>({});
  const [addingNewMemberId, setAddingNewMemberId] = useState<string | null>(null);
  const [renameDialog, setRenameDialog] = useState(false);
  const [newOrganizationName, setNewOrganizationName] = useState('');
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Deep links from the tab era ("?tab=configuration&sub=bundles") mostly
    // map 1:1 onto rail keys; anything unknown lands on Overview.
    const tab = searchParams.get('tab');
    const sub = searchParams.get('sub');
    if (sub && SECTION_KEYS.has(sub)) return sub;
    if (tab && SECTION_KEYS.has(tab)) return tab;
    if (tab === 'configuration') return 'settings';
    if (tab === 'resources') return 'templates';
    return 'overview';
  });
  const [metadataExpanded, setMetadataExpanded] = useState<boolean>(false);

  const {
    data: organization,
    isLoading: orgLoading,
    error: orgError,
    refetch,
  } = useGetV2AdminOrganizationsByOrganizationIdQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId },
  );

  const {
    data: providersData,
    isLoading: providersLoading,
    refetch: refetchProviders,
  } = useGetV2AdminOrganizationsByOrganizationIdProvidersQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId },
  );

  const { data: dashboardUrlData, isLoading: isDashboardUrlLoading } =
    useGetV2AdminOrganizationsByOrganizationIdSubscriptionDashboardUrlQuery(
      { organizationId: organizationId || '' },
      { skip: !organizationId || !organization?.stripe_subscription_id },
    );

  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    refetch: refetchInvoices,
  } = useGetV2AdminOrganizationsByOrganizationIdInvoicesQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId || !organization?.stripe_customer_id },
  );

  const { data: creditsData, refetch: refetchCredits } =
    useGetV2AdminOrganizationsByOrganizationIdCreditsQuery(
      { organizationId: organizationId || '' },
      { skip: !organizationId },
    );

  const { data: discountsData, refetch: refetchDiscounts } =
    useGetV2AdminOrganizationsByOrganizationIdDiscountsQuery(
      { organizationId: organizationId || '' },
      { skip: !organizationId || !organization?.stripe_subscription_id },
    );

  // Adapters: generated API shapes -> the panel view models. Kept here rather
  // than widening the generated types, per the codegen contract.
  const creditTransactions: CreditTransactionRow[] = useMemo(
    () =>
      (creditsData?.transactions ?? []).map(txn => ({
        id: txn.id,
        // The API already flips Stripe's inverted sign for the balance, but
        // transaction amounts arrive raw, so flip them here to match.
        amountCents: -txn.amount,
        endingBalanceCents: -txn.ending_balance,
        description: txn.description ?? null,
        createdAt: epochToIso(txn.created),
      })),
    [creditsData],
  );

  const discountRows: DiscountRow[] = useMemo(
    () =>
      (discountsData?.discounts ?? []).map(d => ({
        id: d.id ?? null,
        couponId: d.coupon_id ?? null,
        couponName: d.coupon_name ?? null,
        percentOff: d.percent_off ?? null,
        amountOffCents: d.amount_off ?? null,
        duration: (d.duration as DiscountRow['duration']) ?? null,
        durationInMonths: d.duration_in_months ?? null,
        endsAt: epochToIso(d.end),
        reason: d.reason ?? null,
        isAdminApplied: d.is_admin_applied ?? false,
      })),
    [discountsData],
  );

  const itemDiscountRows: ItemDiscountRow[] = useMemo(
    () =>
      (discountsData?.item_discounts ?? []).map(d => ({
        id: d.id ?? null,
        couponId: d.coupon_id ?? null,
        couponName: d.coupon_name ?? null,
        percentOff: d.percent_off ?? null,
        amountOffCents: d.amount_off ?? null,
        duration: (d.duration as DiscountRow['duration']) ?? null,
        durationInMonths: d.duration_in_months ?? null,
        endsAt: epochToIso(d.end),
        reason: d.reason ?? null,
        subscriptionItemId: d.subscription_item_id ?? null,
        productName: d.product_name ?? null,
        isAdminApplied: d.is_admin_applied ?? false,
      })),
    [discountsData],
  );

  const lineItemRows: SubscriptionLineItem[] = useMemo(
    () =>
      (discountsData?.line_items ?? []).map(i => ({
        subscriptionItemId: i.subscription_item_id,
        productName: i.product_name ?? null,
        quantity: i.quantity ?? null,
        unitAmountCents: i.unit_amount ?? null,
        hasDiscount: i.has_discount ?? false,
      })),
    [discountsData],
  );

  const invoiceRows: InvoiceRow[] = useMemo(
    () =>
      (invoicesData?.invoices ?? []).map(inv => ({
        id: inv.id,
        number: inv.number ?? null,
        createdAt: inv.created ?? '',
        totalCents: inv.total ?? 0,
        status: (inv.status as InvoiceRow['status']) ?? 'draft',
        hostedUrl: inv.hosted_invoice_url ?? null,
        chargeId: inv.charge_id ?? null,
        chargeAmountCents: inv.charge_amount ?? null,
        amountRefundedCents: inv.amount_refunded ?? null,
      })),
    [invoicesData],
  );

  // Stripe rejects cancelling a subscription that has already ended, so only
  // offer the action while the subscription is still running.
  const subscriptionStatus: string | undefined = organization?.stripe_subscription_details?.status;
  const isSubscriptionCancelable =
    !!subscriptionStatus &&
    ['active', 'trialing', 'past_due', 'unpaid'].includes(subscriptionStatus);
  const currentPeriodEndLabel = organization?.stripe_subscription_details?.current_period_end
    ? formatDateTime(new Date(organization.stripe_subscription_details.current_period_end * 1000))
    : null;

  const [mergeOrganizations] = usePostV2AdminOrganizationsByOrganizationIdMergeMutation();
  const [removeMember] = useDeleteV2AdminOrganizationsByOrganizationIdMembersAndUserIdMutation();
  const [addUserRole] = usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation();
  const [reactivateUser] =
    usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdReactivateMutation();
  const [updateUserRoles] =
    usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation();
  const [syncSubscription, { isLoading: isSyncing }] =
    usePostV2AdminOrganizationsByOrganizationIdSyncSubscriptionMutation();
  const [extendTrial, { isLoading: isExtendingTrial }] =
    usePostV2AdminOrganizationsByOrganizationIdExtendTrialMutation();
  const [createSubscription, { isLoading: isCreatingSubscription }] =
    usePostV2AdminOrganizationsByOrganizationIdCreateSubscriptionMutation();
  const [cancelSubscription, { isLoading: isCancelingSubscription }] =
    usePostV2AdminOrganizationsByOrganizationIdCancelSubscriptionMutation();
  const [reactivateSubscription, { isLoading: isReactivatingSubscription }] =
    usePostV2AdminOrganizationsByOrganizationIdReactivateSubscriptionMutation();
  const [applyCredit, { isLoading: isApplyingCredit }] =
    usePostV2AdminOrganizationsByOrganizationIdCreditsMutation();
  const [applyDiscount, { isLoading: isApplyingDiscount }] =
    usePostV2AdminOrganizationsByOrganizationIdDiscountsMutation();
  const [removeDiscount] = useDeleteV2AdminOrganizationsByOrganizationIdDiscountsMutation();
  const [editDiscount, { isLoading: isEditingDiscount }] =
    usePatchV2AdminOrganizationsByOrganizationIdDiscountsMutation();
  const [issueRefund, { isLoading: isRefunding }] =
    usePostV2AdminOrganizationsByOrganizationIdRefundsMutation();
  const [updateOrganization, { isLoading: isUpdatingOrg }] =
    usePatchV2AdminOrganizationsByOrganizationIdMutation();
  const [updatePatientVisibility, { isLoading: isUpdatingVisibility }] =
    usePatchV2AdminOrganizationsByOrganizationIdPatientVisibilityMutation();

  const {
    data: patientVisibility,
    isLoading: isLoadingVisibility,
    refetch: refetchVisibility,
  } = useGetV2AdminOrganizationsByOrganizationIdPatientVisibilityQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId },
  );

  const { data: searchResults, isLoading: searchLoading } = useSearchProvidersQuery(searchQuery, {
    skip: !hasSearched || searchQuery.trim().length === 0,
  });

  // Get active and inactive users from organization data
  const { activeUsers, inactiveUsers } = useMemo(() => {
    if (!organization?.users) return { activeUsers: [], inactiveUsers: [] };

    const transformUser = (user: any) => ({
      provider_id: user.user_id,
      user_id: user.user_id,
      name: user.name || '',
      email: user.email || '',
      roles: user.roles || [],
      status: user.status || 'active',
      deactivated_at: user.deactivated_at || null,
      is_historical: user.is_historical || false,
      payment_status: user.payment_status || null,
      user_info: user.user_info || null,
      user_metrics: user.user_metrics || null,
      // Map notes_created from user_metrics if available
      note_count: user.user_metrics?.notes_created || 0,
    });

    const active: any[] = [];
    const inactive: any[] = [];

    organization.users.forEach(user => {
      const transformedUser = transformUser(user);
      if (user.status === 'inactive') {
        inactive.push(transformedUser);
      } else {
        active.push(transformedUser);
      }
    });

    return { activeUsers: active, inactiveUsers: inactive };
  }, [organization]);

  // Member id → name for the Activity timeline (the analytics store carries
  // ids only, by design; names come from data the page already fetched).
  const memberNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const user of organization?.users ?? []) {
      if (user.user_id && user.name) names[user.user_id] = user.name;
    }
    return names;
  }, [organization]);

  const handleProviderClick = useCallback(
    (provider: any) => {
      navigate(`/admin-v2/provider/${provider.provider_id}`);
    },
    [navigate],
  );

  const toggleRowExpanded = useCallback((providerId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  }, []);

  const handleRemoveRole = useCallback(
    async (userId: string, role?: string) => {
      if (!organizationId) return;

      const operationKey = `remove-${userId}-${role || 'all'}`;
      setLoadingOperations(prev => ({ ...prev, [operationKey]: true }));

      try {
        const result = await removeMember({
          organizationId,
          userId,
          role: role || undefined, // Pass specific role or undefined (not null) to deactivate entirely
        }).unwrap();

        if (result.success) {
          enqueueSnackbar(result.message || 'Role removed successfully', { variant: 'success' });
          refetch(); // Refresh organization data
        }
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to remove role', { variant: 'error' });
      } finally {
        setLoadingOperations(prev => ({ ...prev, [operationKey]: false }));
      }

      setConfirmDialog({ open: false });
    },
    [organizationId, removeMember, enqueueSnackbar, refetch, refetchProviders],
  );

  const handleAddRole = useCallback(
    async (userId: string, role: string) => {
      if (!organizationId) return;

      const operationKey = `add-${userId}-${role}`;
      setLoadingOperations(prev => ({ ...prev, [operationKey]: true }));

      try {
        const result = await addUserRole({
          organizationId,
          userId,
          addUserRoleRequest: {
            role,
          },
        }).unwrap();

        if (result.success) {
          enqueueSnackbar(result.message || 'Role added successfully', { variant: 'success' });
          refetch(); // Refresh organization data
        }
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to add role', { variant: 'error' });
      } finally {
        setLoadingOperations(prev => ({ ...prev, [operationKey]: false }));
      }
    },
    [organizationId, addUserRole, enqueueSnackbar, refetch, refetchProviders],
  );

  const handleAddNewMember = useCallback(
    async (userId: string) => {
      if (!organizationId) return;

      setAddingNewMemberId(userId);

      try {
        // Use the merge endpoint to transfer the user from their personal org to this org
        // The backend will automatically find the user's organization and assign the roles
        const result = await mergeOrganizations({
          organizationId, // Target organization that will receive the member
          mergeOrganizationsRequest: {
            source_user_id: userId, // User whose org should be merged
            roles: [selectedRole], // Roles to assign in the target organization
          },
        }).unwrap();

        if (result.success) {
          enqueueSnackbar(result.message || 'Member added successfully', { variant: 'success' });
          refetch(); // Refresh organization data
          setAddMemberDialog(false);
          setSearchQuery('');
          setHasSearched(false);
        }
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to add member', { variant: 'error' });
      } finally {
        setAddingNewMemberId(null);
      }
    },
    [organizationId, selectedRole, mergeOrganizations, enqueueSnackbar, refetch, refetchProviders],
  );

  const handleReactivateUser = useCallback(
    async (userId: string) => {
      if (!organizationId) return;

      const operationKey = `reactivate-${userId}`;
      setLoadingOperations(prev => ({ ...prev, [operationKey]: true }));

      try {
        const result = await reactivateUser({
          organizationId,
          userId,
        }).unwrap();

        if (result.success) {
          enqueueSnackbar(result.message || 'User reactivated successfully', {
            variant: 'success',
          });
          refetch(); // Refresh organization data
        }
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to reactivate user', { variant: 'error' });
      } finally {
        setLoadingOperations(prev => ({ ...prev, [operationKey]: false }));
      }
    },
    [organizationId, reactivateUser, enqueueSnackbar, refetch],
  );

  const handleSyncSubscription = useCallback(async () => {
    if (!organizationId) return;

    try {
      const result = await syncSubscription({ organizationId }).unwrap();

      if (result.success) {
        enqueueSnackbar(result.message || 'Subscription synced successfully', {
          variant: 'success',
        });
        // Refresh the page data
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to sync subscription', { variant: 'error' });
    }
  }, [organizationId, syncSubscription, enqueueSnackbar, refetch, refetchProviders]);

  const handleExtendTrial = useCallback(async () => {
    if (!organizationId || !trialDaysToAdd) return;

    try {
      const result = await extendTrial({
        organizationId,
        extendTrialRequest: { days: trialDaysToAdd },
      }).unwrap();

      if (result.success) {
        enqueueSnackbar(result.message || `Trial extended by ${trialDaysToAdd} days`, {
          variant: 'success',
        });
        setExtendTrialDialog(false);
        setTrialDaysToAdd(7); // Reset to default
        // Refresh the page data
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to extend trial', { variant: 'error' });
    }
  }, [organizationId, trialDaysToAdd, extendTrial, enqueueSnackbar, refetch]);

  const handleRenameOrganization = useCallback(async () => {
    if (!organizationId || !newOrganizationName.trim()) return;

    try {
      const result = await updateOrganization({
        organizationId,
        updateOrganizationRequest: {
          name: newOrganizationName.trim(),
        },
      }).unwrap();

      if (result.success) {
        enqueueSnackbar(result.message || 'Organization renamed successfully', {
          variant: 'success',
        });
        setRenameDialog(false);
        refetch(); // Refresh organization data
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to rename organization', {
        variant: 'error',
      });
    }
  }, [organizationId, newOrganizationName, updateOrganization, enqueueSnackbar, refetch]);

  const handleCreateSubscription = useCallback(async () => {
    if (!organizationId) return;

    try {
      const result = await createSubscription({
        organizationId,
        createSubscriptionRequest: {
          trial_days: subscriptionTrialDays,
        },
      }).unwrap();

      if (result.success) {
        enqueueSnackbar(result.message || 'Subscription created successfully', {
          variant: 'success',
        });
        setCreateSubscriptionDialog(false);
        setSubscriptionTrialDays(null); // Reset to default
        refetch(); // Refresh organization data
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to create subscription', {
        variant: 'error',
      });
    }
  }, [organizationId, subscriptionTrialDays, createSubscription, enqueueSnackbar, refetch]);

  const closeCancelSubscriptionDialog = useCallback(() => {
    setCancelSubscriptionDialog(false);
    setCancelAtPeriodEnd(true);
    setCancellationReason('');
    setCancelConfirmText('');
  }, []);

  const handleCancelSubscription = useCallback(async () => {
    if (!organizationId || cancelConfirmText !== 'CANCEL') return;

    try {
      const result = await cancelSubscription({
        organizationId,
        adminCancelSubscriptionRequest: {
          cancel_at_period_end: cancelAtPeriodEnd,
          cancellation_reason: cancellationReason.trim() || undefined,
        },
      }).unwrap();

      if (result.success) {
        enqueueSnackbar(result.message || 'Subscription canceled', { variant: 'success' });
        closeCancelSubscriptionDialog();
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to cancel subscription', {
        variant: 'error',
      });
    }
  }, [
    organizationId,
    cancelConfirmText,
    cancelAtPeriodEnd,
    cancellationReason,
    cancelSubscription,
    enqueueSnackbar,
    refetch,
    closeCancelSubscriptionDialog,
  ]);

  const handleReactivateSubscription = useCallback(async () => {
    if (!organizationId) return;

    try {
      const result = await reactivateSubscription({ organizationId }).unwrap();

      if (result.success) {
        enqueueSnackbar(result.message || 'Subscription reactivated', { variant: 'success' });
        refetch();
      }
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || 'Failed to reactivate subscription', {
        variant: 'error',
      });
    }
  }, [organizationId, reactivateSubscription, enqueueSnackbar, refetch]);

  const handleApplyCredit = useCallback(
    async (amountCents: number, reason: string) => {
      if (!organizationId) return;

      try {
        const result = await applyCredit({
          organizationId,
          adminApplyCreditRequest: { amount: amountCents, reason },
        }).unwrap();

        enqueueSnackbar(result.message || 'Credit applied', { variant: 'success' });
        setApplyCreditDialog(false);
        refetchCredits();
        // Credit draws down against the next invoice, so the upcoming total
        // shown above is now wrong until it reloads.
        refetchInvoices();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to apply credit', { variant: 'error' });
      }
    },
    [organizationId, applyCredit, enqueueSnackbar, refetchCredits, refetchInvoices],
  );

  const handleApplyDiscount = useCallback(
    async (input: {
      percentOff: number | null;
      amountOffCents: number | null;
      duration: 'once' | 'repeating' | 'forever';
      durationInMonths: number | null;
      reason: string;
      customerLabel: string;
      subscriptionItemId: string | null;
      replaceExisting: boolean;
    }) => {
      if (!organizationId) return;

      try {
        const result = await applyDiscount({
          organizationId,
          adminApplyDiscountRequest: {
            percent_off: input.percentOff,
            amount_off: input.amountOffCents,
            duration: input.duration,
            duration_in_months: input.durationInMonths,
            reason: input.reason,
            customer_label: input.customerLabel,
            subscription_item_id: input.subscriptionItemId,
            replace_existing: input.replaceExisting,
          },
        }).unwrap();

        enqueueSnackbar(result.message || 'Discount applied', { variant: 'success' });
        setApplyDiscountDialog(false);
        refetchDiscounts();
        refetchInvoices();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to apply discount', { variant: 'error' });
      }
    },
    [organizationId, applyDiscount, enqueueSnackbar, refetchDiscounts, refetchInvoices],
  );

  const handleRemoveDiscount = useCallback(
    async (subscriptionItemId?: string) => {
      if (!organizationId) return;

      try {
        const result = await removeDiscount({
          organizationId,
          subscriptionItemId,
        }).unwrap();
        enqueueSnackbar(result.message || 'Discount removed', { variant: 'success' });
        refetchDiscounts();
        refetchInvoices();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to remove discount', { variant: 'error' });
      }
    },
    [organizationId, removeDiscount, enqueueSnackbar, refetchDiscounts, refetchInvoices],
  );

  const handleEditDiscountLabels = useCallback(
    async (input: { customerLabel: string; reason: string }) => {
      if (!organizationId || !editingDiscount) return;

      try {
        const result = await editDiscount({
          organizationId,
          adminEditDiscountRequest: {
            customer_label: input.customerLabel,
            reason: input.reason,
            subscription_item_id: (editingDiscount as ItemDiscountRow).subscriptionItemId ?? null,
          },
        }).unwrap();

        enqueueSnackbar(result.message || 'Discount labels updated', {
          variant: 'success',
        });
        setEditingDiscount(null);
        refetchDiscounts();
        // The customer label shows on the invoice preview too.
        refetchInvoices();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to update labels', {
          variant: 'error',
        });
      }
    },
    [
      organizationId,
      editingDiscount,
      editDiscount,
      enqueueSnackbar,
      refetchDiscounts,
      refetchInvoices,
    ],
  );

  const handleRefund = useCallback(
    async (input: {
      chargeId: string;
      amountCents: number;
      reason: 'duplicate' | 'fraudulent' | 'requested_by_customer';
      note: string;
    }) => {
      if (!organizationId) return;

      try {
        const result = await issueRefund({
          organizationId,
          adminRefundRequest: {
            charge_id: input.chargeId,
            amount: input.amountCents,
            reason: input.reason,
            note: input.note,
          },
        }).unwrap();

        enqueueSnackbar(result.message || 'Refund issued', { variant: 'success' });
        setRefundInvoice(null);
        // The invoice list carries refund state, so it has to reload for the
        // row badge and the next prefill to be right.
        refetchInvoices();
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to issue refund', { variant: 'error' });
      }
    },
    [organizationId, issueRefund, enqueueSnackbar, refetchInvoices],
  );

  const handleOpenStripeDashboard = useCallback(() => {
    if (dashboardUrlData?.dashboard_url) {
      window.open(dashboardUrlData.dashboard_url, '_blank', 'noopener,noreferrer');
    }
  }, [dashboardUrlData]);

  const handlePatientVisibilityChange = useCallback(
    async (newMode: string) => {
      if (!organizationId) return;

      try {
        const result = await updatePatientVisibility({
          organizationId,
          updatePatientVisibilityRequest: {
            mode: newMode,
          },
        }).unwrap();

        if (result.success) {
          enqueueSnackbar(result.message || 'Patient visibility updated successfully', {
            variant: 'success',
          });
          refetchVisibility();
        }
      } catch (error: any) {
        enqueueSnackbar(error?.data?.message || 'Failed to update patient visibility', {
          variant: 'error',
        });
      }
    },
    [organizationId, updatePatientVisibility, enqueueSnackbar, refetchVisibility],
  );

  const handleSectionSelect = useCallback(
    (key: string) => {
      // Some rail entries are links to other pages, not sections of this one —
      // including Resource Explorer, which became its own route in GDN-2723.
      const linkTarget = orgRailDestination(key, organizationId ?? '');
      if (linkTarget) {
        navigate(linkTarget);
        return;
      }
      setActiveSection(key);
      // Keep the section in the URL so refresh/back/deep-links stay correct.
      // Drop legacy sub + explorer params on section switch; they belong to
      // the section being left.
      const next = new URLSearchParams(searchParams);
      next.set('tab', key);
      next.delete('sub');
      for (const param of ['rtype', 'rid', 'rpatient', 'rprovider', 'rpage', 'dsession']) {
        next.delete(param);
      }
      setSearchParams(next);
    },
    [navigate, organizationId, searchParams, setSearchParams],
  );

  const renderProviderExpandedContent = useCallback(
    (provider: any) => {
      const userInfo = provider.user_info;
      const userMetrics = provider.user_metrics;
      const userId = provider.provider_id || provider.user_id;
      const isActive = provider.status !== 'inactive';
      const deactivateOperationKey = `deactivate-${userId}`;
      const isDeactivating = loadingOperations[deactivateOperationKey];

      return (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                <Person className="h-4 w-4 mr-1" />
                User Information
              </h4>
              <dl className="space-y-1 text-sm">
                <div className="flex items-center">
                  <dt className="text-gray-500 w-24">Name:</dt>
                  <dd className="text-gray-900">{userInfo?.name || 'Not provided'}</dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500 w-24 flex items-center">
                    <Email className="h-3 w-3 mr-1" />
                    Email:
                  </dt>
                  <dd className="text-gray-900">
                    {userInfo?.email || provider.email || 'Not provided'}
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500 w-24 flex items-center">
                    <Phone className="h-3 w-3 mr-1" />
                    Phone:
                  </dt>
                  <dd className="text-gray-900">{userInfo?.mobile_phone_num || 'Not provided'}</dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500 w-24">Profile:</dt>
                  <dd className="text-gray-900">
                    {userInfo?.profile_picture ? (
                      <img
                        src={userInfo.profile_picture}
                        alt="Profile"
                        className="h-8 w-8 rounded-full"
                      />
                    ) : (
                      'No profile picture'
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Metrics & Details</h4>
              <dl className="space-y-1 text-sm">
                <div className="flex items-center">
                  <dt className="text-gray-500 w-32">Provider ID:</dt>
                  <dd className="text-gray-900 font-mono text-xs">
                    {provider.provider_id || provider.user_id}
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500 w-32">Notes Created:</dt>
                  <dd className="text-gray-900">
                    {userMetrics?.notes_created || provider.note_count || 0}
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500 w-32">Email Verified:</dt>
                  <dd className="text-gray-900">
                    {provider.email_verified ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <Cancel className="h-4 w-4 text-gray-400" />
                    )}
                  </dd>
                </div>
                <div className="flex items-center">
                  <dt className="text-gray-500 w-32">Created:</dt>
                  <dd className="text-gray-900">
                    {provider.created_at
                      ? formatDateTime(new Date(provider.created_at))
                      : 'Unknown'}
                  </dd>
                </div>
                {provider.deactivated_at && (
                  <div className="flex items-center">
                    <dt className="text-gray-500 w-32">Deactivated:</dt>
                    <dd className="text-gray-900">
                      {formatDateTime(new Date(provider.deactivated_at))}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Action buttons for active users */}
          {isActive && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex gap-2 justify-between items-center">
                {/* Per-member membership settings: co-sign (clinicians),
                prescribing proxy (operations_support). */}
                <div className="flex flex-wrap gap-x-4">
                  {provider.roles?.includes(ROLES.CLINICIAN) && organizationId && (
                    <AdminCosignToggle organizationId={organizationId} userId={userId} />
                  )}
                  {provider.roles?.includes(ROLES.OPERATIONS_SUPPORT) && organizationId && (
                    <PrescribingProxyToggle organizationId={organizationId} userId={userId} />
                  )}
                </div>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  disabled={isDeactivating}
                  onClick={() => {
                    setConfirmDialog({
                      open: true,
                      userId: userId,
                      role: undefined,
                      action: 'remove',
                    });
                  }}
                  startIcon={isDeactivating ? <CircularProgress size={16} /> : <Cancel />}
                >
                  {isDeactivating ? 'Deactivating...' : 'Deactivate User'}
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    },
    [loadingOperations, setConfirmDialog],
  );

  // Columns for inactive users (simplified with reactivate button)
  const inactiveUserColumns = useMemo(
    () => [
      {
        key: 'expand' as any,
        label: '',
        render: (_: unknown, provider: any) => (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              toggleRowExpanded(provider.provider_id);
            }}
          >
            {expandedRows.has(provider.provider_id) ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        ),
      },
      {
        key: 'name' as any,
        label: 'Provider Name',
        render: (value: unknown, provider: any) => (
          <div className="flex items-center">
            <Person className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-500">{String(value || 'Unknown')}</span>
            {provider.is_historical && (
              <Tooltip title="Historical member: exists so migrated records attribute to this clinician. Cannot log in or be reactivated.">
                <Chip label="Historical" size="small" sx={{ ml: 1 }} />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        key: 'roles' as any,
        label: 'Previous Roles',
        render: (value: unknown, provider: any) => {
          const roles = provider.roles as string[];
          return (
            <div className="flex gap-1 flex-wrap items-center">
              {roles.map((role, index) => (
                <Chip
                  key={index}
                  label={getRoleDisplayName(role)}
                  color="default"
                  size="small"
                  sx={{ opacity: 0.6 }}
                />
              ))}
              {roles.length === 0 && (
                <span className="text-gray-400 text-sm">No previous roles</span>
              )}
            </div>
          );
        },
      },
      {
        key: 'email' as any,
        label: 'Email',
        render: (value: unknown) => (
          <span className="text-gray-500">{String(value || 'No email')}</span>
        ),
      },
      {
        key: 'deactivated_at' as any,
        label: 'Deactivated',
        render: (value: unknown) => (
          <span className="text-gray-500">
            {value ? formatRelativeTime(new Date(String(value))) : 'Unknown'}
          </span>
        ),
      },
      {
        key: 'note_count' as any,
        label: 'Notes',
        render: (value: unknown) => <span className="text-gray-500">{String(value || 0)}</span>,
      },
      {
        key: 'provider_id' as any,
        label: 'Actions',
        render: (_: unknown, provider: any) => {
          const userId = provider.provider_id || provider.user_id;
          const isLoading = loadingOperations[`reactivate-${userId}`];

          if (provider.is_historical) {
            // Historical members are permanently inactive; the server refuses
            // reactivation with 409 — surface why instead of a dead button.
            return (
              <Tooltip title="Historical member — cannot be reactivated">
                <span>
                  <Button size="small" variant="contained" color="success" disabled>
                    Reactivate
                  </Button>
                </span>
              </Tooltip>
            );
          }

          return (
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={isLoading}
              onClick={e => {
                e.stopPropagation();
                handleReactivateUser(userId);
              }}
              startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isLoading ? 'Reactivating...' : 'Reactivate'}
            </Button>
          );
        },
      },
    ],
    [toggleRowExpanded, expandedRows, handleReactivateUser, loadingOperations],
  );

  const providerColumns = useMemo(
    () => [
      {
        key: 'expand' as any,
        label: '',
        render: (_: unknown, provider: any) => (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();
              toggleRowExpanded(provider.provider_id);
            }}
          >
            {expandedRows.has(provider.provider_id) ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        ),
      },
      {
        key: 'name' as any,
        label: 'Provider Name',
        render: (value: unknown) => (
          <div className="flex items-center">
            <Person className="h-5 w-5 text-gray-400 mr-2" />
            <span className="font-medium text-gray-900">{String(value || 'Unknown')}</span>
          </div>
        ),
      },
      {
        key: 'roles' as any,
        label: 'Roles',
        render: (value: unknown, provider: any) => {
          const roles = provider.roles as string[];
          // Check if there are any roles that could be added
          const allPossibleRoles = [
            ROLES.CLINICIAN,
            ROLES.OPERATIONS_SUPPORT,
            ROLES.PRACTICE_ADMINISTRATOR,
            ROLES.SUPERVISOR,
            ROLES.BILLING_ADMINISTRATOR,
          ];
          const canAddRole = allPossibleRoles.some(role => !roles.includes(role));

          return (
            <div className="flex gap-1 flex-wrap items-center">
              {roles.map((role, index) => {
                const color = getRoleBadgeColor(role);
                const canRemove = true;
                const isOnlyRole = roles.length === 1;
                const userId = provider.provider_id || provider.user_id;
                const operationKey = `remove-${userId}-${isOnlyRole ? 'all' : role}`;
                const isLoading = loadingOperations[operationKey];

                return (
                  <Chip
                    key={index}
                    label={getRoleDisplayName(role)}
                    color={color}
                    size="small"
                    disabled={isLoading}
                    onDelete={
                      canRemove && !isLoading
                        ? () => {
                            setConfirmDialog({
                              open: true,
                              userId: userId,
                              role: isOnlyRole ? undefined : role, // If only role, remove from org entirely
                              action: 'remove',
                            });
                          }
                        : undefined
                    }
                    deleteIcon={
                      canRemove ? (
                        isLoading ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <Close style={{ fontSize: 16 }} />
                        )
                      ) : undefined
                    }
                  />
                );
              })}

              {canAddRole && (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={e => {
                      e.stopPropagation();
                      setRoleMenuAnchor(e.currentTarget);
                      setRoleMenuProviderId(provider.provider_id || provider.user_id);
                    }}
                    title="Add role"
                  >
                    <Add style={{ fontSize: 16 }} />
                  </IconButton>
                </>
              )}
            </div>
          );
        },
      },
      {
        key: 'payment_status' as keyof AdminProviderResponse,
        label: 'Payment Status',
        render: (value: unknown) => {
          const status = value as string;
          let color: 'success' | 'warning' | 'error' | 'default' = 'default';
          let icon = undefined;

          switch (status) {
            case 'active':
            case 'trialing':
              color = 'success';
              icon = <CheckCircle className="h-4 w-4 mr-1" />;
              break;
            case 'canceled':
            case 'cancelled':
              color = 'error';
              icon = <Cancel className="h-4 w-4 mr-1" />;
              break;
            case 'past_due':
              color = 'warning';
              break;
          }

          return <Chip label={status || 'unknown'} color={color} size="small" icon={icon} />;
        },
      },
      {
        key: 'note_count' as keyof AdminProviderResponse,
        label: 'Notes',
        render: (value: unknown) => <span className="text-gray-600">{String(value || 0)}</span>,
      },
      {
        key: 'provider_id' as keyof AdminProviderResponse,
        label: 'Actions',
        render: (_: unknown, provider: any) => (
          <Button
            size="small"
            onClick={e => {
              e.stopPropagation();
              navigate(`/admin-v2/provider/${provider.provider_id}`);
            }}
          >
            View Details
          </Button>
        ),
      },
    ],
    [navigate, expandedRows, toggleRowExpanded, loadingOperations],
  );

  if (!organizationId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Organization not found</h2>
            <p className="text-gray-600 mt-2">No organization ID specified.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (orgLoading) {
    return <LoadingState message="Loading organization details..." />;
  }

  if (orgError || !organization) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">Organization not found</h2>
            <p className="text-gray-600 mt-2">Unable to load organization information.</p>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-gray-50">
      {/* Workspace takeover: this sidebar renders in the global admin
          sidebar's exact slot with its exact shell (AdminLayout hides the
          global one here) — the takeover swaps the sidebar's content, not
          its chrome. Its header is the global → org context stack. */}
      <WorkspaceSidebar
        groups={NAV_GROUPS}
        activeKey={activeSection}
        onSelect={handleSectionSelect}
        contextStack={[
          { label: 'Admin v2', onSelect: () => navigate('/admin-v2') },
          {
            label: organization?.name || 'Organization',
            chip: 'Org',
            onSelect: () => handleSectionSelect('overview'),
          },
        ]}
      />

      <div className="min-w-0 flex-1 overflow-y-auto">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Passport: everything that identifies the org (name, status, the
            IDs admins constantly copy) plus org-level actions, in one calm
            sticky band. */}
          <OrgPassport
            identity={{
              organizationId,
              name: organization.name,
              createdAt: organization.created_at ?? null,
              stripeCustomerId: organization.stripe_customer_id ?? null,
              stripeSubscriptionId: organization.stripe_subscription_id ?? null,
              dosespotClinicId: organization.dosespot_clinic_id ?? null,
            }}
            subscriptionState={toSubscriptionState(organization.stripe_subscription_details)}
            onRename={() => {
              setNewOrganizationName(organization.name || '');
              setRenameDialog(true);
            }}
            onOpenStripe={
              organization.stripe_subscription_id ? handleOpenStripeDashboard : undefined
            }
            onCancelSubscription={
              isSubscriptionCancelable &&
              !organization.stripe_subscription_details?.cancel_at_period_end
                ? () => setCancelSubscriptionDialog(true)
                : undefined
            }
          />

          <div className="py-6">
            {/* Overview: subscription at a glance, then member management, then
            org metadata — the cleaned-up header area + old Members tab. */}
            {activeSection === 'overview' && (
              <>
                <Box sx={{ mb: 3 }}>
                  <OrganizationSubscriptionCard
                    organization={organization}
                    organizationId={organizationId}
                    onSync={handleSyncSubscription}
                    isSyncing={isSyncing}
                    onExtendTrial={() => setExtendTrialDialog(true)}
                    onReactivate={handleReactivateSubscription}
                    isReactivating={isReactivatingSubscription}
                    onCreateSubscription={() => setCreateSubscriptionDialog(true)}
                  />
                </Box>

                {/* Read-only member summary — management lives in the
                    Members section. */}
                <Box sx={{ mb: 3 }}>
                  <OrganizationMembersList
                    members={activeUsers}
                    inactiveCount={inactiveUsers.length}
                    onOpenMember={id => navigate(`/admin-v2/provider/${id}`)}
                    onManage={() => handleSectionSelect('members')}
                  />
                </Box>

                {/* Metadata is rarely needed at a glance — hide it behind a
              toggle so the top section stays focused on Subscription. */}
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setMetadataExpanded(prev => !prev)}
                    startIcon={metadataExpanded ? <ExpandLess /> : <ExpandMore />}
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Metadata
                  </Button>
                  <Collapse in={metadataExpanded}>
                    <Card sx={{ mt: 1 }}>
                      <CardContent>
                        {organization.group_info ? (
                          <pre className="text-xs text-gray-700 overflow-auto max-h-32">
                            {JSON.stringify(organization.group_info, null, 2)}
                          </pre>
                        ) : (
                          <p className="text-sm text-gray-500">No additional metadata</p>
                        )}
                      </CardContent>
                    </Card>
                  </Collapse>
                </Box>
              </>
            )}

            {/* Members: the full management surface — tables with role
                editing, invitations, and the role-permissions reference. */}
            {activeSection === 'members' && (
              <>
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">Organization Members</h2>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="contained"
                      startIcon={<PersonAdd />}
                      onClick={() => setAddMemberDialog(true)}
                    >
                      Add Member
                    </Button>
                  </div>
                </div>

                {/* Active Users Table */}
                <ExpandableDataTable
                  title=""
                  columns={providerColumns}
                  data={activeUsers}
                  total={activeUsers.length}
                  loading={orgLoading}
                  onRowClick={handleProviderClick}
                  emptyMessage="No active members found"
                  expandedRows={expandedRows}
                  getRowId={(provider: any) => provider.provider_id || provider.user_id}
                  renderExpandedContent={renderProviderExpandedContent as any}
                />

                {/* Inactive Users Section */}
                {inactiveUsers.length > 0 && (
                  <Card sx={{ mt: 3 }}>
                    <CardHeader
                      title={
                        <Button
                          variant="text"
                          onClick={() => setInactiveUsersExpanded(!inactiveUsersExpanded)}
                          startIcon={inactiveUsersExpanded ? <ExpandLess /> : <ExpandMore />}
                          sx={{
                            justifyContent: 'flex-start',
                            textTransform: 'none',
                            color: 'text.secondary',
                            fontSize: '1rem',
                            fontWeight: 'medium',
                          }}
                        >
                          Inactive Members ({inactiveUsers.length})
                        </Button>
                      }
                    />
                    <Collapse in={inactiveUsersExpanded}>
                      <CardContent sx={{ pt: 0 }}>
                        <ExpandableDataTable
                          title=""
                          columns={inactiveUserColumns}
                          data={inactiveUsers}
                          total={inactiveUsers.length}
                          loading={false}
                          onRowClick={handleProviderClick}
                          emptyMessage="No inactive members"
                          expandedRows={expandedRows}
                          getRowId={(provider: any) => provider.provider_id || provider.user_id}
                          renderExpandedContent={renderProviderExpandedContent as any}
                        />
                      </CardContent>
                    </Collapse>
                  </Card>
                )}

                <Box sx={{ mt: 3 }}>
                  <OrganizationInvitationsSection organizationId={organizationId || ''} />
                </Box>

                {/* Role permissions reference (read-only). Editing lives in Configuration → Settings. */}
                <Box sx={{ mt: 3 }}>
                  <OrganizationRolePermissionsSection organizationId={organizationId} mode="read" />
                </Box>
              </>
            )}

            {/* Subscription: the full money view — same card plus invoices.
            ("Billing" is reserved for the patient-billing / JotBill domain.) */}
            {activeSection === 'subscription' && (
              <>
                <OrganizationSubscriptionCard
                  organization={organization}
                  organizationId={organizationId}
                  onSync={handleSyncSubscription}
                  isSyncing={isSyncing}
                  onExtendTrial={() => setExtendTrialDialog(true)}
                  onReactivate={handleReactivateSubscription}
                  isReactivating={isReactivatingSubscription}
                  onCreateSubscription={() => setCreateSubscriptionDialog(true)}
                />

                <OrganizationUpcomingInvoiceCard upcoming={invoicesData?.upcoming} />

                {/* Credit before discount: it's the default remedy and, unlike
                    a discount, it can't silently displace something else. */}
                <Box sx={{ mt: 2 }}>
                  <CreditBalanceCard
                    creditBalanceCents={
                      creditsData?.has_stripe_customer ? (creditsData.credit_balance ?? 0) : null
                    }
                    transactions={creditTransactions}
                    onApplyCredit={() => setApplyCreditDialog(true)}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <DiscountCard
                    discounts={discountRows}
                    itemDiscounts={itemDiscountRows}
                    hasSubscription={!!organization.stripe_subscription_id}
                    onApplyDiscount={() => setApplyDiscountDialog(true)}
                    onRemoveDiscount={() => handleRemoveDiscount()}
                    onRemoveItemDiscount={d =>
                      handleRemoveDiscount(d.subscriptionItemId ?? undefined)
                    }
                    onEditDiscount={setEditingDiscount}
                  />
                </Box>

                {organization.stripe_customer_id && (
                  <Card sx={{ mt: 2 }}>
                    <CardHeader disableTypography title={<CardTitle>Past invoices</CardTitle>} />
                    <CardContent>
                      {invoicesLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2" color="text.secondary">
                            Loading invoices…
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          {(invoicesData?.invoices?.length ?? 0) === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              No invoices yet.
                            </Typography>
                          ) : (
                            <div className="divide-y divide-gray-100">
                              {invoicesData!.invoices!.map(inv => (
                                <div
                                  key={inv.id}
                                  className="flex items-center gap-3 py-1.5 text-sm"
                                >
                                  <span className="w-36 shrink-0 text-gray-700">
                                    {inv.created ? formatDateTime(new Date(inv.created)) : '—'}
                                  </span>
                                  <span className="w-28 shrink-0 font-mono text-xs text-gray-500">
                                    {inv.number || inv.id}
                                  </span>
                                  <span className="w-24 shrink-0 text-right font-medium">
                                    {formatCents(inv.total)}
                                  </span>
                                  <Chip
                                    label={inv.status}
                                    size="small"
                                    color={
                                      inv.status === 'paid'
                                        ? 'success'
                                        : inv.status === 'open'
                                          ? 'warning'
                                          : inv.status === 'uncollectible'
                                            ? 'error'
                                            : 'default'
                                    }
                                  />
                                  <span className="flex-1" />
                                  {(() => {
                                    const row = invoiceRows.find(r => r.id === inv.id);
                                    return row ? (
                                      <InvoiceRefundAction
                                        invoice={row}
                                        onRefund={setRefundInvoice}
                                      />
                                    ) : null;
                                  })()}
                                  {inv.hosted_invoice_url && (
                                    <Tooltip title="View invoice in Stripe">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          window.open(
                                            inv.hosted_invoice_url || '',
                                            '_blank',
                                            'noopener,noreferrer',
                                          )
                                        }
                                      >
                                        <OpenInNew fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </div>
                              ))}
                              {invoicesData?.has_more && (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                  sx={{ display: 'block', pt: 1 }}
                                >
                                  Showing the {invoicesData.invoices?.length} most recent — see the
                                  Stripe Dashboard for older invoices.
                                </Typography>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                )}

                <OrganizationBillingHistoryCard organizationId={organizationId} />
              </>
            )}

            {/* Activity: the org's recent event timeline from the analytics
                store — every module's exhaust in one place. */}
            {activeSection === 'activity' && (
              <RecentActivityCard organizationId={organizationId} userNames={memberNames} />
            )}

            {activeSection === 'settings' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Patient Visibility Setting */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Visibility sx={{ fontSize: 20, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                      Patient Visibility Mode
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Control how patients are shared across the organization
                  </Typography>
                  {isLoadingVisibility ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} />
                      <Typography variant="body2" color="text.secondary">
                        Loading...
                      </Typography>
                    </Box>
                  ) : (
                    <FormControl fullWidth size="small">
                      <Select
                        value={patientVisibility?.mode || 'provider_only'}
                        onChange={e => handlePatientVisibilityChange(e.target.value)}
                        disabled={isUpdatingVisibility}
                        startAdornment={
                          isUpdatingVisibility ? (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                          ) : null
                        }
                      >
                        <MenuItem value="provider_only">
                          <Box>
                            <Typography variant="body2">Provider Only</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Each provider can only see their own patients
                            </Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="organization_wide">
                          <Box>
                            <Typography variant="body2">Organization Wide</Typography>
                            <Typography variant="caption" color="text.secondary">
                              All providers can see all patients in the organization
                            </Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>
                <Divider />
                <OrganizationRolePermissionsSection organizationId={organizationId} mode="edit" />
              </Box>
            )}
            {activeSection === 'bundles' && (
              <OrganizationBundleAssignmentsSection organizationId={organizationId || ''} />
            )}
            {activeSection === 'feature-gates' && (
              <OrganizationStatsigGatesSection organizationId={organizationId || ''} />
            )}
            {activeSection === 'policies' && (
              <OrganizationPoliciesSection organizationId={organizationId || ''} />
            )}
            {activeSection === 'dosespot' && (
              <OrganizationDosespotPatientsSection organizationId={organizationId || ''} />
            )}
            {activeSection === 'jotaudit' && (
              <AdminJotAuditDashboardEmbedded organizationId={organizationId || ''} />
            )}

            {activeSection === 'templates' && (
              <OrganizationTemplatesSection organizationId={organizationId || ''} />
              /* TODO: Re-enable OrganizationFormsSection when forms ship alongside templates. */
            )}
            {activeSection === 'encounter-types' && (
              <OrganizationEncounterTypesSection organizationId={organizationId || ''} />
            )}
            {activeSection === 'payer-docs' && (
              <OrganizationPayerDocsSection organizationId={organizationId || ''} />
            )}
            {activeSection === 'clinic-docs' && (
              <Box sx={{ height: 'calc(100vh - 300px)', minHeight: 500 }}>
                <AdminClinicDocumentsEmbedded organizationId={organizationId || ''} />
              </Box>
            )}
            {activeSection === 'intake-hub' && (
              <AdminIntakeHubEmbedded
                organizationId={organizationId || ''}
                providers={activeUsers}
              />
            )}

            {/* Data Import — the v2 agentic import pipeline (GDN-1947): raw
            uploads → staged batches → review + commit. */}
            {activeSection === 'data-import' && (
              <DataImportSection organizationId={organizationId || ''} />
            )}

            {/* Data Export — whole-org clinical data export (GDN-1983): trigger a
            background export, watch it run, download the finished ZIP. */}
            {activeSection === 'data-export' && (
              <DataExportSection organizationId={organizationId || ''} />
            )}

            {activeSection === 'billing' && (
              <OrganizationBillingSection organizationId={organizationId || ''} />
            )}

            {activeSection === 'billing-tasks' && (
              <OrganizationBillingTasksSection organizationId={organizationId || ''} />
            )}

            {/* Metrics — the practice-owner metrics grid scoped to this org,
            plus the affordance for recording intervention markers. */}
            {activeSection === 'metrics' && (
              <OrganizationMetricsSection organizationId={organizationId || ''} />
            )}
          </div>

          {/* Confirmation Dialog */}
          <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false })}>
            <DialogTitle>
              {confirmDialog.action === 'remove'
                ? confirmDialog.role
                  ? 'Remove Role'
                  : 'Deactivate User'
                : 'Add Role'}
            </DialogTitle>
            <DialogContent>
              <Typography>
                {confirmDialog.action === 'remove'
                  ? confirmDialog.role
                    ? `Are you sure you want to remove the "${confirmDialog.role}" role from this user?`
                    : `Are you sure you want to deactivate this user? They will be moved to the inactive members list and can be reactivated later.`
                  : `Are you sure you want to add the "${confirmDialog.role}" role to this user?`}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmDialog({ open: false })}>Cancel</Button>
              <Button
                onClick={() => {
                  if (confirmDialog.action === 'remove' && confirmDialog.userId) {
                    handleRemoveRole(confirmDialog.userId, confirmDialog.role);
                  }
                  setConfirmDialog({ open: false });
                }}
                color="error"
                variant="contained"
              >
                {confirmDialog.action === 'remove'
                  ? confirmDialog.role
                    ? 'Remove Role'
                    : 'Deactivate User'
                  : 'Add'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Add Member Dialog */}
          <Dialog
            open={addMemberDialog}
            onClose={() => setAddMemberDialog(false)}
            fullWidth
            maxWidth={'sm'}
          >
            <DialogTitle>Add Member to Organization</DialogTitle>
            <DialogContent>
              <Box className="space-y-4 pt-4">
                <TextField
                  fullWidth
                  label="Search by email or name"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      setHasSearched(true);
                    }
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    value={selectedRole}
                    onChange={e => setSelectedRole(e.target.value)}
                    label="Role"
                  >
                    <MenuItem value={ROLES.CLINICIAN}>
                      <Box>
                        <Typography>{getRoleDisplayName(ROLES.CLINICIAN)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Healthcare provider who creates clinical notes
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value={ROLES.OPERATIONS_SUPPORT}>
                      <Box>
                        <Typography>{getRoleDisplayName(ROLES.OPERATIONS_SUPPORT)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Administrative staff supporting operations
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value={ROLES.PRACTICE_ADMINISTRATOR}>
                      <Box>
                        <Typography>{getRoleDisplayName(ROLES.PRACTICE_ADMINISTRATOR)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Manages organization, billing, and users
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value={ROLES.SUPERVISOR}>
                      <Box>
                        <Typography>{getRoleDisplayName(ROLES.SUPERVISOR)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Clinical supervisor overseeing providers
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem value={ROLES.BILLING_ADMINISTRATOR}>
                      <Box>
                        <Typography>{getRoleDisplayName(ROLES.BILLING_ADMINISTRATOR)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Manages billing and financial aspects
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setHasSearched(true)}
                  disabled={!searchQuery.trim()}
                >
                  Search Users
                </Button>

                {searchLoading && <Typography className="text-center">Searching...</Typography>}

                {searchResults && searchResults.length > 0 && (
                  <Box className="border rounded p-2 max-h-60 overflow-y-auto">
                    {searchResults.map((user: any) => {
                      const isAlreadyMember =
                        activeUsers.some(u => u.provider_id === user.provider_id) ||
                        inactiveUsers.some(u => u.provider_id === user.provider_id);
                      return (
                        <Box
                          key={user.provider_id}
                          className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                        >
                          <Box>
                            <Typography variant="body1">{user.name || 'Unknown'}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {user.email}
                            </Typography>
                          </Box>
                          {isAlreadyMember ? (
                            <Chip label="Already member" size="small" />
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              disabled={addingNewMemberId === user.provider_id}
                              onClick={() => handleAddNewMember(user.provider_id)}
                              startIcon={
                                addingNewMemberId === user.provider_id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : null
                              }
                            >
                              {addingNewMemberId === user.provider_id ? 'Adding...' : 'Add'}
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {hasSearched && searchResults && searchResults.length === 0 && (
                  <Typography className="text-center text-gray-500">
                    No users found matching "{searchQuery}"
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setAddMemberDialog(false);
                  setSearchQuery('');
                  setHasSearched(false);
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {/* Role Picker Menu */}
          <Menu
            anchorEl={roleMenuAnchor}
            open={Boolean(roleMenuAnchor && roleMenuProviderId)}
            onClose={() => {
              setRoleMenuAnchor(null);
              setRoleMenuProviderId(null);
            }}
            slotProps={{
              paper: {
                style: {
                  maxHeight: 350,
                  width: '280px',
                },
              },
            }}
          >
            {roleMenuProviderId &&
              (() => {
                const provider =
                  activeUsers.find(u => u.provider_id === roleMenuProviderId) ||
                  inactiveUsers.find(u => u.provider_id === roleMenuProviderId);
                const existingRoles = (provider?.roles as string[]) || [];

                // Define all available roles (current roles only)
                const allRoles = [
                  ROLES.CLINICIAN,
                  ROLES.OPERATIONS_SUPPORT,
                  ROLES.PRACTICE_ADMINISTRATOR,
                  ROLES.SUPERVISOR,
                  ROLES.BILLING_ADMINISTRATOR,
                ];

                // Filter out roles the user already has
                const availableRoles = allRoles.filter(role => !existingRoles.includes(role));

                if (availableRoles.length === 0) {
                  return (
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">
                        User has all available roles
                      </Typography>
                    </MenuItem>
                  );
                }

                return (
                  <>
                    <MenuItem disabled>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 'bold' }}
                      >
                        Select a role to add:
                      </Typography>
                    </MenuItem>
                    <Divider />
                    {availableRoles.map(role => {
                      const operationKey = `add-${roleMenuProviderId}-${role}`;
                      const isLoading = loadingOperations[operationKey];

                      return (
                        <MenuItem
                          key={role}
                          disabled={isLoading}
                          onClick={() => {
                            handleAddRole(roleMenuProviderId, role);
                            setRoleMenuAnchor(null);
                            setRoleMenuProviderId(null);
                          }}
                          sx={{ py: 1 }}
                        >
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}
                          >
                            {isLoading ? (
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                }}
                              >
                                <Chip
                                  label={getRoleDisplayName(role)}
                                  size="small"
                                  color={getRoleBadgeColor(role)}
                                  sx={{ minWidth: 150 }}
                                />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ mt: 0.5, ml: 0.5 }}
                                >
                                  {role === ROLES.CLINICIAN &&
                                    'Healthcare provider who creates notes'}
                                  {role === ROLES.OPERATIONS_SUPPORT &&
                                    'Administrative support staff'}
                                  {role === ROLES.PRACTICE_ADMINISTRATOR &&
                                    'Manages org and billing'}
                                  {role === ROLES.SUPERVISOR && 'Clinical supervisor'}
                                  {role === ROLES.BILLING_ADMINISTRATOR && 'Manages billing'}
                                </Typography>
                              </Box>
                            )}
                            {isLoading && (
                              <Typography variant="caption" color="text.secondary">
                                Adding...
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </>
                );
              })()}
          </Menu>

          {/* Rename Organization Dialog */}
          <Dialog
            open={renameDialog}
            onClose={() => setRenameDialog(false)}
            fullWidth
            maxWidth={'sm'}
          >
            <DialogTitle>Rename Organization</DialogTitle>
            <DialogContent>
              <Box className="pt-4">
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={newOrganizationName}
                  onChange={e => setNewOrganizationName(e.target.value)}
                  autoFocus
                  helperText="Enter the new name for this organization"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setRenameDialog(false);
                  setNewOrganizationName('');
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRenameOrganization}
                color="primary"
                variant="contained"
                disabled={
                  isUpdatingOrg ||
                  !newOrganizationName.trim() ||
                  newOrganizationName.trim() === organization?.name
                }
                startIcon={isUpdatingOrg ? <CircularProgress size={16} /> : null}
              >
                {isUpdatingOrg ? 'Renaming...' : 'Rename'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Extend Trial Dialog */}
          <Dialog
            open={extendTrialDialog}
            onClose={() => setExtendTrialDialog(false)}
            fullWidth
            maxWidth={'xs'}
          >
            <DialogTitle>Extend Trial Period</DialogTitle>
            <DialogContent>
              <Box className="space-y-4 pt-4">
                <Typography variant="body2" color="textSecondary">
                  Current trial ends:{' '}
                  {organization?.stripe_subscription_details?.trial_end
                    ? formatDateTime(
                        new Date(organization.stripe_subscription_details.trial_end * 1000),
                      )
                    : 'Unknown'}
                </Typography>

                <TextField
                  fullWidth
                  label="Days to add"
                  type="number"
                  value={trialDaysToAdd}
                  onChange={e => {
                    const value = parseInt(e.target.value);
                    if (value >= 1 && value <= 365) {
                      setTrialDaysToAdd(value);
                    }
                  }}
                  helperText="Enter number of days to extend the trial (1-365)"
                  slotProps={{ htmlInput: { min: 1, max: 365 } }}
                />

                <Typography variant="body2">
                  New trial end date will be:{' '}
                  {organization?.stripe_subscription_details?.trial_end && (
                    <strong>
                      {formatDateTime(
                        new Date(
                          organization.stripe_subscription_details.trial_end * 1000 +
                            trialDaysToAdd * 24 * 60 * 60 * 1000,
                        ),
                      )}
                    </strong>
                  )}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setExtendTrialDialog(false);
                  setTrialDaysToAdd(7);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleExtendTrial}
                color="primary"
                variant="contained"
                disabled={isExtendingTrial || !trialDaysToAdd}
                startIcon={isExtendingTrial ? <CircularProgress size={16} /> : null}
              >
                {isExtendingTrial ? 'Extending...' : `Extend by ${trialDaysToAdd} days`}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Create Subscription Dialog */}
          <Dialog
            open={createSubscriptionDialog}
            onClose={() => setCreateSubscriptionDialog(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Create New Subscription</DialogTitle>
            <DialogContent>
              <Box className="space-y-4 pt-4">
                <Typography variant="body2" color="textSecondary">
                  This will create a new subscription for the organization. The price and quantity
                  will be set automatically by the backend.
                </Typography>

                <TextField
                  fullWidth
                  label="Trial Days (Optional)"
                  type="number"
                  value={subscriptionTrialDays || ''}
                  onChange={e => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    if (value === null || (value >= 0 && value <= 365)) {
                      setSubscriptionTrialDays(value);
                    }
                  }}
                  helperText="Leave empty to use default trial period from environment, or enter 0-365 days"
                  slotProps={{ htmlInput: { min: 0, max: 365 } }}
                />

                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                  Note: Price ID and quantity are automatically configured based on the
                  organization's settings.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setCreateSubscriptionDialog(false);
                  setSubscriptionTrialDays(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateSubscription}
                color="primary"
                variant="contained"
                disabled={isCreatingSubscription}
                startIcon={isCreatingSubscription ? <CircularProgress size={16} /> : null}
              >
                {isCreatingSubscription ? 'Creating...' : 'Create Subscription'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Cancel Subscription Dialog */}
          <Dialog
            open={cancelSubscriptionDialog}
            // Ignore backdrop/escape dismissal while the cancel is in flight, so
            // the dialog can't be closed out from under an unfinished request.
            onClose={isCancelingSubscription ? undefined : closeCancelSubscriptionDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogContent>
              <Box className="space-y-4 pt-4">
                <Typography variant="body2">
                  Are you sure you want to cancel the subscription for{' '}
                  <strong>{organization?.name}</strong>?
                </Typography>

                <FormControl>
                  <RadioGroup
                    value={cancelAtPeriodEnd ? 'period_end' : 'immediate'}
                    onChange={e => setCancelAtPeriodEnd(e.target.value === 'period_end')}
                  >
                    <FormControlLabel
                      value="period_end"
                      control={<Radio />}
                      label={
                        <Box className="py-1">
                          <Typography variant="body2">Cancel at period end</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {currentPeriodEndLabel
                              ? `Keeps access until ${currentPeriodEndLabel}.`
                              : 'Keeps access until the end of the current billing period.'}{' '}
                            This matches what users get when they cancel from the app, and it can be
                            undone.
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="immediate"
                      control={<Radio />}
                      label={
                        <Box className="py-1">
                          <Typography variant="body2">Cancel immediately</Typography>
                          <Typography variant="caption" color="textSecondary">
                            Revokes access right away. This cannot be undone — a new subscription
                            would have to be created.
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                {!cancelAtPeriodEnd && (
                  <Alert severity="warning">
                    Access will be revoked immediately for every member of this organization.
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label="Reason (optional)"
                  value={cancellationReason}
                  onChange={e => setCancellationReason(e.target.value)}
                  multiline
                  minRows={2}
                  helperText="Recorded on the Stripe subscription for future reference"
                  slotProps={{ htmlInput: { maxLength: 500 } }}
                />

                <TextField
                  fullWidth
                  label="Type CANCEL to confirm"
                  value={cancelConfirmText}
                  onChange={e => setCancelConfirmText(e.target.value)}
                  error={cancelConfirmText.length > 0 && cancelConfirmText !== 'CANCEL'}
                  helperText="This is a paying customer action — type CANCEL (all caps) to enable the button"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCancelSubscriptionDialog} disabled={isCancelingSubscription}>
                Keep Subscription
              </Button>
              <Button
                onClick={handleCancelSubscription}
                color="error"
                variant="contained"
                disabled={isCancelingSubscription || cancelConfirmText !== 'CANCEL'}
                startIcon={isCancelingSubscription ? <CircularProgress size={16} /> : null}
              >
                {isCancelingSubscription
                  ? 'Canceling...'
                  : cancelAtPeriodEnd
                    ? 'Cancel at Period End'
                    : 'Cancel Immediately'}
              </Button>
            </DialogActions>
          </Dialog>

          <ApplyCreditDialog
            open={applyCreditDialog}
            organizationName={organization?.name || 'this organization'}
            isSubmitting={isApplyingCredit}
            onClose={() => setApplyCreditDialog(false)}
            onSubmit={handleApplyCredit}
          />

          <ApplyDiscountDialog
            open={applyDiscountDialog}
            organizationName={organization?.name || 'this organization'}
            existingDiscounts={discountRows}
            itemDiscounts={itemDiscountRows}
            lineItems={lineItemRows}
            isSubmitting={isApplyingDiscount}
            onClose={() => setApplyDiscountDialog(false)}
            onSubmit={handleApplyDiscount}
          />

          <EditDiscountLabelsDialog
            open={!!editingDiscount}
            discount={editingDiscount}
            isSubmitting={isEditingDiscount}
            onClose={() => setEditingDiscount(null)}
            onSubmit={handleEditDiscountLabels}
          />

          <RefundDialog
            open={!!refundInvoice}
            invoice={refundInvoice}
            isSubmitting={isRefunding}
            onClose={() => setRefundInvoice(null)}
            onSubmit={handleRefund}
          />
        </main>
      </div>
    </div>
  );
}
